import { useQuery } from '@tanstack/react-query';
import { ArrowRight, BarChart3, MessageSquare, Plus, Video } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '~/components/ui/skeleton';
import { api } from '~/lib/api';
import { i18n } from '~/lib/i18n';

type ActivityTimelineProps = {
  orgId: string;
  deptId: string;
  jobId: string;
  applicationId: string;
  stageNames: Map<string, string>;
};

type ActivityEventItem = {
  id: string;
  applicationId: string;
  candidateId: string;
  eventType: string;
  payload: Record<string, unknown>;
  actorType: string;
  actorId: string | null;
  occurredAt: string;
};

function activityQueryOptions(orgId: string, deptId: string, jobId: string, applicationId: string) {
  return {
    queryKey: ['activity', orgId, deptId, jobId, applicationId] as const,
    queryFn: async () => {
      const response = await api.api.org[':orgId'].department[':deptId'].jobs[
        ':jobId'
      ].applications[':applicationId'].activity.$get({
        param: { orgId, deptId, jobId, applicationId },
        query: {}
      });
      if (!response.ok) throw new Error(i18n.t('errors.unexpected'));
      return response.json();
    }
  };
}

function EventIcon({ eventType }: { eventType: string }): React.ReactNode {
  const className = 'size-4';
  switch (eventType) {
    case 'stage_change':
      return <ArrowRight className={className} />;
    case 'interview_completed':
      return <Video className={className} />;
    case 'score_received':
      return <BarChart3 className={className} />;
    case 'note_added':
      return <MessageSquare className={className} />;
    case 'application_created':
      return <Plus className={className} />;
    default:
      return <Plus className={className} />;
  }
}

function EventMessage({
  event,
  stageNames
}: {
  event: ActivityEventItem;
  stageNames: Map<string, string>;
}): React.ReactNode {
  const { t } = useTranslation();

  switch (event.eventType) {
    case 'stage_change': {
      const fromName =
        stageNames.get(event.payload.fromStageId as string) ?? t('activity.bySystem');
      const toName = stageNames.get(event.payload.toStageId as string) ?? t('activity.bySystem');
      return <span>{t('activity.stageChange', { from: fromName, to: toName })}</span>;
    }
    case 'interview_completed':
      return <span>{t('activity.interviewCompleted')}</span>;
    case 'score_received':
      return (
        <span>{t('activity.scoreReceived', { score: event.payload.overallScore as number })}</span>
      );
    case 'application_created':
      return <span>{t('activity.applicationCreated')}</span>;
    case 'note_added':
      return <span>{t('activity.noteAdded')}</span>;
    default:
      return <span>{event.eventType}</span>;
  }
}

function ActorLabel({ actorType }: { actorType: string }): React.ReactNode {
  const { t } = useTranslation();
  if (actorType === 'system')
    return <span className="text-muted-foreground">{t('activity.bySystem')}</span>;
  if (actorType === 'ai')
    return <span className="text-muted-foreground">{t('activity.byAi')}</span>;
  return null;
}

export function ActivityTimeline({
  orgId,
  deptId,
  jobId,
  applicationId,
  stageNames
}: ActivityTimelineProps): React.ReactNode {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    ...activityQueryOptions(orgId, deptId, jobId, applicationId),
    enabled: !!orgId && !!deptId
  });

  const events: ActivityEventItem[] = data?.events ?? [];

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <h3 className="mb-4 text-base font-semibold">{t('activity.title')}</h3>
      {events.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('activity.empty')}</p>
      ) : (
        <div className="flex flex-col">
          {events.map((event, idx) => (
            <div key={event.id} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className="flex size-8 items-center justify-center rounded-full bg-secondary text-muted-foreground">
                  <EventIcon eventType={event.eventType} />
                </div>
                {idx < events.length - 1 && <div className="w-px flex-1 bg-border" />}
              </div>
              <div className="flex flex-col gap-0.5 pb-4">
                <div className="flex items-center gap-2 text-sm">
                  <EventMessage event={event} stageNames={stageNames} />
                  <ActorLabel actorType={event.actorType} />
                </div>
                <time className="text-xs text-muted-foreground">
                  {new Date(event.occurredAt).toLocaleString()}
                </time>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
